'use client';

import type { MutableRefObject, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useClearRefinements } from 'react-instantsearch';

import { FILTERS } from '@libs/algolia';

type PriceRangeState = {
    minValue: number;
    maxValue: number;
    hasChanged: boolean;
    rangeIsLoaded: MutableRefObject<boolean> | null;
    pendingAnalytics: MutableRefObject<boolean>;
    setMinValue: (value: number) => void;
    setMaxValue: (value: number) => void;
    clearRange: (callback?: () => void) => void;
};

const noopPendingAnalytics = { current: false };

const initialState: PriceRangeState = {
    minValue: FILTERS.RANGE.MIN_VALUE,
    maxValue: FILTERS.RANGE.MAX_VALUE,
    hasChanged: false,
    rangeIsLoaded: null,
    pendingAnalytics: noopPendingAnalytics,
    setMinValue: () => null,
    setMaxValue: () => null,
    clearRange: () => null,
};

const PriceRangeContext = createContext<PriceRangeState>(initialState);

export default function PriceRangeProvider({ children }: { children: ReactNode }) {
    const { refine } = useClearRefinements({
        includedAttributes: [FILTERS.RANGE.ATTRIBUTE],
    });

    const [minValue, setMinValue] = useState<number>(FILTERS.RANGE.MIN_VALUE);
    const [maxValue, setMaxValue] = useState<number>(FILTERS.RANGE.MAX_VALUE);

    const hasChanged = !(minValue === FILTERS.RANGE.MIN_VALUE && maxValue === FILTERS.RANGE.MAX_VALUE);

    const rangeIsLoaded = useRef<boolean>(false);
    const pendingAnalytics = useRef(false);

    const clearRange = useCallback(
        (callback?: () => void) => {
            pendingAnalytics.current = false;
            setMinValue(FILTERS.RANGE.MIN_VALUE);
            setMaxValue(FILTERS.RANGE.MAX_VALUE);
            refine();
            setTimeout(() => {
                if (callback) {
                    callback();
                }
            }, 1);
        },
        [refine]
    );

    const value = useMemo(
        () => ({
            minValue,
            maxValue,
            hasChanged,
            rangeIsLoaded,
            pendingAnalytics,
            setMinValue,
            setMaxValue,
            clearRange,
        }),
        [minValue, maxValue, hasChanged, clearRange]
    );

    return <PriceRangeContext.Provider value={value}>{children}</PriceRangeContext.Provider>;
}

export const usePriceRangeContext = () => useContext<PriceRangeState>(PriceRangeContext);
